import {
  Avatar,
  FlexBox,
  Spinner,
  Table,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { PayloadAction } from '@reduxjs/toolkit';
import { ampli } from 'lib-shared-tracking';
import {
  ConversationAndRequester,
  SearchUserHit,
  User,
} from 'lib-shared-types-api';
import { MouseEvent, useCallback, useMemo } from 'react';

import { AwayStatus } from 'apis/presence';
import {
  ConversationsTableConversationRow,
  ConversationsTableHeadRow,
} from 'components/conversation/conversationTable';
import { CustomAwayStatus } from 'components/presence/components/customAwayStatus';
import { getUserDisplayName } from 'utils/user';

import { canMakeInternalCall, getWorkingChannels } from '../../utils';
import { PresencePopover } from '../presencePopover';
import { TeamCard } from '../teamCard';

import { Actions, TeamChannelIconsList } from './components';

const getStatus = (member: SearchUserHit): 'working' | 'away' | 'offline' =>
  member.is_online ? (member.is_working ? 'working' : 'away') : 'offline';

const onActionsDropdownClick = () => {
  ampli.teamAgentActionsMenuOpened();
};

type TeamTableRowProps = {
  member: SearchUserHit;
  isFavorite: boolean;
  toggleFavorite: () => void;
  isOwnAgent: boolean;
  hasPresenceAccess: boolean;
  isExpanded: boolean;
  hasOrganizationMemberAccess: boolean;
  hasConversationReplyAccess: boolean;
  isMicrophoneBlocked: boolean;
  twilioMakeInternalCall: (userId: string) => Promise<void>;
  showUserModal: (
    user: Partial<User>,
  ) => PayloadAction<{ user: Partial<User> }, string>;
  onToggle: (e: MouseEvent<HTMLElement>) => void;
  conversations: readonly ConversationAndRequester[];
  conversationsLoading: boolean;
  awayStatuses: AwayStatus[];
};

export const TeamTableRow = ({
  member,
  isFavorite,
  toggleFavorite,
  isOwnAgent,
  hasPresenceAccess,
  isExpanded,
  hasOrganizationMemberAccess,
  hasConversationReplyAccess,
  isMicrophoneBlocked,
  twilioMakeInternalCall,
  showUserModal,
  onToggle,
  conversations,
  conversationsLoading,
  awayStatuses,
}: TeamTableRowProps): JSX.Element => {
  const onCallClick = useCallback(async () => {
    ampli.teamAgentActionsMenuItemClicked({ type: 'call' });
    try {
      await twilioMakeInternalCall(member.id);
    } catch {
      // Silently ignore errors
    }
  }, [member.id, twilioMakeInternalCall]);

  const onEditClick = useCallback(() => {
    const user: Partial<User> = {
      id: member.id,
      org_id: member.organization_id,
      name: member.display_name,
      family_name: member.family_name,
      given_name: member.given_name,
      company_id: member.company_id,
      email: member.email,
      additional_emails: member.additional_emails?.map((x) => x),
      phone_number: member.phone_number,
      default_outbound_number: member.phone_number,
      additional_phone_numbers: member.additional_phone_numbers?.map((x) => x),
      type: member.user_type,
      avatar_url: member.avatar_url,
    };
    showUserModal(user);
    ampli.teamAgentActionsMenuItemClicked({ type: 'edit' });
  }, [member, showUserModal]);

  const hasActivePhoneConversation: boolean =
    (member.workloads_by_channel?.pstn_phone || 0) > 0;
  const isInACW: boolean = member?.has_acw || false;
  const simultaneousRingEnabled: boolean = member.simultaneous_ring ?? false;
  const canCall = canMakeInternalCall({
    member,
    isOwnAgent,
    isMicrophoneBlocked,
  });

  const status = getStatus(member);
  const workingChannels = getWorkingChannels(member);

  const isOpen = isExpanded;

  const customAwayStatus = useMemo(() => {
    if (status !== 'away' || !awayStatuses.length) {
      return null;
    }

    const awayStatus = awayStatuses.find(
      ({ id }) => id === member.custom_away_status?.custom_away_status_id,
    );

    if (!awayStatus) {
      return null;
    }

    return (
      <CustomAwayStatus
        awayStatus={awayStatus}
        statusUpdatedAt={member.custom_away_status?.status_updated_at || null}
        showLabel={true}
      />
    );
  }, [status, awayStatuses, member.custom_away_status]);

  const memberName = `${getUserDisplayName(member)}${isOwnAgent ? ' (you)' : ''}`;

  return (
    <TeamCard isOpen={isOpen}>
      <TeamCard.Header
        iconsList={
          <div>
            <TeamChannelIconsList
              workloads_by_channel={member.workloads_by_channel}
              working_media_types={member.working_media_types}
              workload_config={member.workload_config}
              status={status}
              simultaneousRingEnabled={simultaneousRingEnabled}
            />
            {hasPresenceAccess && (
              <PresencePopover
                user={{
                  id: member.id,
                  name: member.display_name || '',
                }}
                presence={{
                  status: status,
                  working_channels: workingChannels,
                }}
              />
            )}
          </div>
        }
        actions={
          isOwnAgent ||
          hasOrganizationMemberAccess ||
          hasConversationReplyAccess ? (
            <Actions
              onActionsDropdownClick={onActionsDropdownClick}
              hasConversationReplyAccess={hasConversationReplyAccess}
              hasOrganizationMemberAccess={hasOrganizationMemberAccess}
              canMakeInternalCall={canCall}
              isOwnAgent={isOwnAgent}
              onCallClick={onCallClick}
              onEditClick={onEditClick}
            />
          ) : undefined
        }
        status={status}
        avatar={
          <Tooltip content={memberName}>
            <Avatar
              user={{
                id: member.id,
                name: memberName,
                avatar_url: member.avatar_url,
              }}
            />
          </Tooltip>
        }
        isFavorite={isFavorite}
        onFavoriteClick={toggleFavorite}
        isOpen={isOpen}
        onToggle={onToggle}
        onPhone={hasActivePhoneConversation}
        simultaneous={simultaneousRingEnabled}
        inAcw={isInACW}
      >
        <FlexBox gap="300" alignItems="center">
          {memberName}
          {customAwayStatus}
        </FlexBox>
      </TeamCard.Header>
      <TeamCard.Content>
        <FlexBox justifyContent="center" alignItems="center" padding="0 300">
          {conversationsLoading ? (
            <Spinner size={16} />
          ) : conversations && conversations.length > 0 ? (
            <Table>
              <Table.Thead>
                <ConversationsTableHeadRow isInitialized={true} />
              </Table.Thead>
              <Table.Tbody>
                {conversations.map((conversationAndRequester) => (
                  <ConversationsTableConversationRow
                    key={conversationAndRequester.conversation.id}
                    conversationAndRequester={conversationAndRequester}
                    showRowMenuFilter={false}
                  />
                ))}
              </Table.Tbody>
            </Table>
          ) : (
            'No conversations found'
          )}
        </FlexBox>
      </TeamCard.Content>
    </TeamCard>
  );
};
